import React, { useEffect, useState, useRef, useCallback } from "react";
import { notification } from "antd";
import { useViewModel } from "mvvm/View";
import { useAppSelector, useDarkMode } from "redux/hooks";
import { JobViewModel } from "../ViewModels/JobViewModel";
import { QuestionSectionView } from "./QuestionSectionView";
import { ResponseSectionView } from "./ResponseSectionView";
import { JobActionButtonsView } from "./JobActionButtonsView";

export function ThreadJobEntryView(props: {
    job: JobViewModel;
    tagList: string[];
    tagUpdater: (jobID: string, tags: string[]) => void;
    onEntryEditModeChange: (jobID: string, isEditMode: boolean) => void;
    registerJobRef: (jobID: string, element: HTMLDivElement | null) => void;
    registerQuestionRef: (jobID: string, element: HTMLDivElement | null) => void;
    parentJobAlias?: string;
    onAnimationStateChange?: (isAnimating: boolean) => void;
    onAddQueryToggle?: (query?: string, queryName?: string) => void;
}) {
    const { job, tagList, tagUpdater, onEntryEditModeChange, registerJobRef, registerQuestionRef, parentJobAlias, onAnimationStateChange, onAddQueryToggle } = props;
    const entryJob = useViewModel(job);
    const { status, statusMessage, outputState } = entryJob;
    const isOutputLoaded = outputState.status === "loaded";
    const loadedOutput = isOutputLoaded ? outputState.output : null;
    const isOutputLoadFailed = outputState.status === "load-failed";
    const outputLoadError = isOutputLoadFailed ? outputState.error : null;
    const darkMode = useDarkMode();
    const agentMemory = useAppSelector(state => state.user.settings?.agent_memory ?? "");
    const defaultPageSize = useAppSelector(state => {
        const pageSizeSetting = state.user.settings?.page_size;
        if (pageSizeSetting) {
            const parsed = typeof pageSizeSetting === 'string' ? parseInt(pageSizeSetting, 10) : pageSizeSetting;
            return isNaN(parsed) ? 100 : parsed;
        }
        return 100;
    });
    const [ellipsis, setEllipsis] = useState("");
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isResponseAnimating, setIsResponseAnimating] = useState(false);
    const jobWrapperRef = useRef<HTMLDivElement | null>(null);
    const responseBoxRef = useRef<HTMLDivElement>(null);
    const tableContainerRef = useRef<HTMLDivElement>(null);

    const registerWrapper = useCallback((node: HTMLDivElement | null) => {
        jobWrapperRef.current = node;
        registerJobRef(entryJob.jobID, node);
    }, [entryJob.jobID, registerJobRef]);

    const handleRegenerate = async () => {
        if (isRegenerating) return;

        setIsRegenerating(true);
        notification.info({
            message: 'Regenerating response',
            description: 'Please wait while the response is being regenerated...',
            placement: 'bottomLeft',
            duration: 0
        });
        try {
            const newJobInfo = await entryJob.page.jobService.regenerateJob(entryJob.jobID, entryJob.question, agentMemory, entryJob.replyTarget);
            await entryJob.page.reloadJobs();
            const newJob = entryJob.page.jobs.find(j => j.jobID === newJobInfo.jobID);
            if (newJob) {
                entryJob.page.selectedJob = newJob;
            }
            notification.destroy();
            notification.success({
                message: 'Response regenerated',
                description: 'The response is being regenerated.',
                placement: 'bottomLeft',
                duration: 2
            });
        } catch (error) {
            notification.destroy();
            notification.error({
                message: 'Failed to regenerate response',
                description: 'Please try again.',
                placement: 'bottomLeft',
                duration: 3
            });
        } finally {
            setIsRegenerating(false);
        }
    };

    const handleEdit = () => {
        if (isEditMode) return;

        setIsEditMode(true);
        onEntryEditModeChange(entryJob.jobID, true);

        setTimeout(() => {
            jobWrapperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
    };

    const handleEditSubmit = async (newText: string) => {
        if (!newText.trim() || newText.trim() === entryJob.question) {
            setIsEditMode(false);
            onEntryEditModeChange(entryJob.jobID, false);
            return;
        }

        setIsEditing(true);
        notification.info({
            message: 'Regenerating request',
            description: 'Please wait while the request is being regenerated...',
            placement: 'bottomLeft',
            duration: 0
        });

        try {
            const newJobInfo = await entryJob.page.jobService.regenerateJob(entryJob.jobID, newText.trim(), agentMemory, entryJob.replyTarget);
            await entryJob.page.reloadJobs();
            const newJob = entryJob.page.jobs.find(j => j.jobID === newJobInfo.jobID);
            if (newJob) {
                entryJob.page.selectedJob = newJob;
            }
            notification.destroy();
            notification.success({
                message: 'Request updated',
                description: 'The request has been updated and is being regenerated.',
                placement: 'bottomLeft',
                duration: 2
            });
        } catch (error) {
            notification.destroy();
            notification.error({
                message: 'Failed to update request',
                description: 'Please try again.',
                placement: 'bottomLeft',
                duration: 3
            });
        } finally {
            setIsEditing(false);
            setIsEditMode(false);
            onEntryEditModeChange(entryJob.jobID, false);
        }
    };

    const handleEditCancel = useCallback(() => {
        setIsEditMode(false);
        onEntryEditModeChange(entryJob.jobID, false);
        notification.info({
            message: 'Edit cancelled',
            description: 'Changes were not saved.',
            placement: 'bottomLeft',
            duration: 2
        });
    }, [entryJob.jobID, onEntryEditModeChange]);

    useEffect(() => {
        tagUpdater(entryJob.jobID, entryJob.tags)
    }, [entryJob.jobID, entryJob.tags, tagUpdater]);

    useEffect(() => {
        const interval = setInterval(() => {
            setEllipsis((prev) => (prev.length < 3 ? prev + "." : ""));
        }, 500);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (responseBoxRef.current && status === "RUNNING" && statusMessage) {
            responseBoxRef.current.scrollTo({
                top: responseBoxRef.current.scrollHeight,
                behavior: 'smooth'
            });
        }
    }, [statusMessage, status]);

    return (
        <div
            ref={registerWrapper}
            style={{ marginBottom: "1.5em" }}
        >
            <QuestionSectionView
                question={entryJob.question}
                isEditMode={isEditMode}
                isEditing={isEditing}
                darkMode={darkMode}
                tagViewModel={entryJob.tagViewModel}
                tagList={tagList}
                onEdit={handleEdit}
                onEditSubmit={handleEditSubmit}
                onEditCancel={handleEditCancel}
                onDelete={() => entryJob.deleteJob()}
                registerQuestionRef={(node) => registerQuestionRef(entryJob.jobID, node)}
                jobWrapperRef={jobWrapperRef}
                isResponseAnimating={isResponseAnimating}
            />
            <ResponseSectionView
                status={status}
                statusMessage={statusMessage}
                outputState={outputState}
                loadedOutput={loadedOutput}
                isOutputLoadFailed={isOutputLoadFailed}
                outputLoadError={outputLoadError}
                darkMode={darkMode}
                parentJobAlias={parentJobAlias}
                defaultPageSize={defaultPageSize}
                jobID={entryJob.jobID}
                job={entryJob}
                responseBoxRef={responseBoxRef}
                tableContainerRef={tableContainerRef}
                ellipsis={ellipsis}
                onAnimationStateChange={(isAnimating) => {
                    setIsResponseAnimating(isAnimating);
                    onAnimationStateChange?.(isAnimating);
                }}
                       onAddQueryToggle={onAddQueryToggle}
            />
            <JobActionButtonsView
                canDownloadOutput={entryJob.canDownloadOutput}
                isOutputLoaded={isOutputLoaded}
                loadedOutput={loadedOutput}
                darkMode={darkMode}
                parentJobAlias={parentJobAlias}
                isRegenerating={isRegenerating}
                onRegenerate={handleRegenerate}
                feedbackUpViewModel={entryJob.feedbackUpViewModel}
                feedbackDownViewModel={entryJob.feedbackDownViewModel}
                jobID={entryJob.jobID}
                jobService={entryJob.page.jobService}
                initialScheduleTime={entryJob.scheduleTime}
                initialScheduleType={entryJob.scheduleType}
                onScheduleUpdate={async () => {
                    await entryJob.page.reloadJobs();
                }}
            />
        </div>
    );
};

